import { FormModel, TranslationService } from '@alfresco/adf-core';
import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';

import { ContentService } from '../../components/shared/services/content.service';
import {
  WkTemplate,
  WkTemplateFormRequest,
} from '../../model/wk-template-model';
import { SnackbarErrorAction } from '../../store/actions/app.custom-actions';
import { AppStore } from '../../store/states/app.state';

@Component({
  selector: 'wk-template-dialog',
  templateUrl: './wk-template.dialog.html',
  styleUrls: ['./wk-template.dialog.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class WkTemplateDialogComponent implements OnInit, OnDestroy {
  public static readonly MANDATORY_FIELD_MISSING =
    'APP.TEMPLATE_DOCUMENT.MANDATORY_FIELD_MISSING';
  public static readonly FAILED_CREATION =
    'APP.TEMPLATE_DOCUMENT.FAILED_CREATION';
  public static readonly FAILED_DUPLICATE =
    'APP.TEMPLATE_DOCUMENT.FAILED_DUPLICATE';
  _onDestroy = new Subject<void>();
  templates: WkTemplate[];
  templateSelected: WkTemplate = null;
  hasTemplates: boolean;
  folderId: string;
  hasFolderId = true;
  hasFormLoaded: boolean;
  formModel: FormModel;
  hasErrors: boolean;
  templateFormName: string;
  templateId: string;
  isLoading = true;
  noTemplateFormShared: boolean;
  filterType: string;
  errorMessage: string;
  mandatoryFields = ['prop_cm_name', 'prop_cm_title'];
  templateForm: any;
  constructor(
    private readonly dialogRef: MatDialogRef<WkTemplateDialogComponent>,
    private readonly contentService: ContentService,
    private readonly router: Router,
    private readonly store: Store<AppStore>,
    private readonly translatationService: TranslationService,
    private readonly changeDetectorRef: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    if (
      this.router.url.includes('libraries') &&
      !this.router.url.includes('preview')
    ) {
      this.folderId = this.router.url.includes('favorite')
        ? this.router.url.slice(1).split('/')[2]
        : this.router.url.slice(1).split('/')[1];
      this.contentService
        .getTemplateList(this.folderId)
        .pipe(
          tap((templates: WkTemplate[]) => {
            templates.sort((a, b) => (a.title < b.title ? -1 : 1));
            this.hasTemplates = templates && templates.length > 0;
          }),

          takeUntil(this._onDestroy)
        )
        .subscribe({
          next: (templates: WkTemplate[]) => {
            this.templates = templates;
            this.isLoading = false;
          },
          error: () => {
            this.templates = [];
            this.isLoading = false;
          },
        });
    } else {
      this.hasFolderId = false;
      this.isLoading = false;
    }
  }

  ngOnDestroy(): void {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  close(): void {
    this.dialogRef.close();
  }

  createHTMLFromTemplate(): void {
    const formProps = {} as WkTemplateFormRequest;
    formProps.properties = {};
    const name =
      this.formModel.values['prop_cm_name'] ||
      this.formModel.values['prop_cm_title'];
    formProps.name = `${name}.html`;
    Object.entries(this.formModel.values).forEach((ele) => {
      if (ele[0] !== 'prop_cm_name' && ele[1]) {
        formProps.properties[ele[0]] = ele[1];
      }
    });
    this.contentService
      .createTemplate(this.folderId, this.templateId, formProps)
      .pipe(takeUntil(this._onDestroy))
      .subscribe({
        next: (docId: string) => {
          this.router.navigateByUrl(`${this.router.url}/preview/${docId}`);
          this.close();
        },
        error: (err) => {
          if (err.message && JSON.parse(err.message).error.statusCode === 409) {
            this.store.dispatch(
              new SnackbarErrorAction(
                WkTemplateDialogComponent.FAILED_DUPLICATE,
                { name: formProps.name }
              )
            );
          } else {
            this.store.dispatch(
              new SnackbarErrorAction(WkTemplateDialogComponent.FAILED_CREATION)
            );
          }
          this.close();
        },
      });
  }

  formLoaded(formModel: FormModel): void {
    this.formModel = formModel;
    this.hasFormLoaded = true;
    this.changeDetectorRef.detectChanges();
  }

  selectTemplate(): void {
    if (this.templateSelected) {
      this.templateFormName = this.templateSelected.name;
      this.templateId = this.templateSelected.id;
      this.hasFormLoaded = false;
      this.noTemplateFormShared = false;
      this.errorMessage = '';
      this.getTemplateFormInfo();
    }
  }

  getTemplateFormInfo() {
    this.contentService
      .getTemplateForm(this.templateId)
      .pipe(takeUntil(this._onDestroy))
      .subscribe({
        next: (form) => {
          this.templateForm = form;
        },
        error: () => {
          this.noTemplateFormShared = true;
          this.resetFormInfo();
        },
      });
  }

  handleError(event: any) {
    if (event === WkTemplateDialogComponent.MANDATORY_FIELD_MISSING) {
      this.resetFormInfo();
      this.errorMessage = this.translatationService.instant(
        WkTemplateDialogComponent.MANDATORY_FIELD_MISSING
      );
    }
  }

  protected resetFormInfo(): void {
    this.templateFormName = '';
    this.templateId = '';
    this.templateForm = null;
  }

  nameLonger(): boolean {
    const name = this.templateSelected.id || this.templateSelected.name;
    return name.split('.')[0].length > 65;
  }
}
